import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS, rolesOf } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NotificationBell } from "@/components/NotificationBell";
import { LogOut, Menu } from "lucide-react";
import { navForUser } from "@/lib/nav";
import { InstallPrompt } from "@/components/InstallPrompt";

function NavItems({ user, onNavigate }) {
  const items = navForUser(user);
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-sidebar-foreground/75 hover:bg-white/5 hover:text-sidebar-foreground"
            }`
          }
        >
          <n.icon className="h-4 w-4 shrink-0" />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3 px-6 py-6">
      <img src="/icon-192.png" alt="Aranya" className="h-10 w-10 rounded-md bg-white object-cover" />
      <div>
        <p className="font-heading text-base font-bold leading-tight text-sidebar-foreground">Aranya Exim</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-sidebar-muted">Pvt Ltd · ERP</p>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex h-full flex-col bg-sidebar">
      <Brand />
      <div className="flex-1 overflow-y-auto pb-6">
        <NavItems user={user} onNavigate={onNavigate} />
      </div>
      <div className="border-t border-white/10 p-4">
        <div className="mb-3 flex items-center gap-3 px-1">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-ochre text-xs font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-sidebar-foreground">{user?.name}</p>
            <p className="truncate text-[11px] text-sidebar-muted">{rolesOf(user).map((r) => ROLE_LABELS[r] || r).join(", ")}</p>
          </div>
          {!onNavigate && <NotificationBell className="ml-auto shrink-0 text-sidebar-foreground/75 hover:bg-white/5 hover:text-sidebar-foreground" />}
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full justify-start gap-2 text-sidebar-foreground/75 hover:bg-white/5 hover:text-sidebar-foreground"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 border-r border-white/10 lg:block">
        <SidebarInner />
      </aside>

      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Aranya" className="h-8 w-8 rounded-md bg-white object-cover" />
          <span className="font-heading font-bold">Aranya Exim</span>
        </div>
        <div className="flex items-center gap-1">
        <NotificationBell />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-0 p-0">
            <SidebarInner onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">{children}</div>
      </main>
      <InstallPrompt />
    </div>
  );
}
